import React, { useEffect, useRef, useState } from 'react';

interface AgbTimeSliderProps {
  year: number;
  minYear: number;
  maxYear: number;
  onChange: (year: number) => void;
  /** True while tiles for the committed year are still streaming in. */
  isLoading?: boolean;
}

// Dwell after a year's tiles finish painting before advancing to the next
// one. This is NOT a fixed interval — the effect below only schedules a
// tick when `isLoading` is false, so cold years effectively stretch to
// `load_time + ADVANCE_MS` while browser-cached years advance at a steady
// ~1500 ms. ~1.5 s per stop gives the eye time to absorb each raster —
// short enough to feel paced, long enough that the brief mid-swap reads
// as a deliberate beat rather than a buffer.
const ADVANCE_MS = 1500;

// Autoplay step. Manual scrubbing (drag / arrow keys on the native range
// input) stays year-by-year, but autoplay jumps in 5-year increments so
// a full 2000 → 2025 sweep is 6 stops instead of 26 — cuts cold-tile
// stalls 4× and lands on canonical demo years (2000, 2005, … 2025).
const AUTOPLAY_STEP_YEARS = 5;

// How long the slider waits after the last drag/keystroke before committing
// a new year to the parent. The `input` event on a native range fires at
// drag-frame rate (~16 ms) — without this debounce, a one-second drag from
// 2000 → 2025 would trigger 25 tile-layer rebuilds in sequence. A settle
// window of ~180 ms catches the end of a normal drag gesture while still
// feeling responsive on a single arrow-key press.
const COMMIT_DEBOUNCE_MS = 180;

// First-visit hint persisted across sessions. The slider sits over the map
// with no inline copy explaining that it scrubs years — a one-time chip
// nudges new users without permanent visual noise once they've used it.
const HINT_STORAGE_KEY = 'sfi.agbSliderHinted';

const readHinted = (): boolean => {
  try {
    return localStorage.getItem(HINT_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
};

const writeHinted = () => {
  try {
    localStorage.setItem(HINT_STORAGE_KEY, '1');
  } catch {
    // ignore quota / privacy-mode failures
  }
};

export const AgbTimeSlider: React.FC<AgbTimeSliderProps> = ({
  year,
  minYear,
  maxYear,
  onChange,
  isLoading = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  // Default "hinted=true" so SSR / initial render doesn't flash the chip
  // before localStorage is read; the effect below flips it on if the
  // user hasn't seen the hint yet.
  const [hinted, setHinted] = useState(true);
  useEffect(() => {
    setHinted(readHinted());
  }, []);
  const dismissHint = () => {
    if (hinted) return;
    writeHinted();
    setHinted(true);
  };
  // Local display year: updates instantly on every drag tick so the slider
  // thumb and the "Year 20xx" label track the user's input. The committed
  // year flows back from the parent; when they differ we're in the brief
  // "pending commit" window between the last drag event and the debounce
  // firing.
  const [localYear, setLocalYear] = useState(year);
  useEffect(() => {
    // Parent is source of truth — if it pushes a new year (e.g. autoplay
    // tick, or external reset), sync local state so the thumb moves.
    setLocalYear(year);
  }, [year]);

  const commitTimer = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const scheduleCommit = (next: number) => {
    if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
    commitTimer.current = window.setTimeout(() => {
      commitTimer.current = null;
      onChangeRef.current(next);
    }, COMMIT_DEBOUNCE_MS);
  };

  const commitNow = (next: number) => {
    if (commitTimer.current !== null) {
      window.clearTimeout(commitTimer.current);
      commitTimer.current = null;
    }
    onChangeRef.current(next);
  };

  // Flush any pending commit when the component unmounts so the parent
  // isn't left with a stale year if the user navigates away mid-drag.
  useEffect(() => {
    return () => {
      if (commitTimer.current !== null) {
        window.clearTimeout(commitTimer.current);
        commitTimer.current = null;
      }
    };
  }, []);

  // The interval callback closes over `localYear`; using a ref avoids
  // tearing down and rebuilding the interval on every year tick.
  const localYearRef = useRef(localYear);
  useEffect(() => {
    localYearRef.current = localYear;
  }, [localYear]);

  // Adaptive-pacing autoplay: wait until `isLoading` flips false (the
  // current year's tiles have painted) and only then schedule the next
  // advance. Cold years take load + ADVANCE_MS; browser-cached years
  // return Leaflet's `load` event near-instantly and advance every
  // ADVANCE_MS. Prevents the slider "Year" label lapping the map raster
  // during cold playback.
  useEffect(() => {
    if (!isPlaying) return;
    if (isLoading) return; // hold the tick until the current year paints
    const id = window.setTimeout(() => {
      // Wrap when already at the ceiling; otherwise advance 5 years but
      // clamp to maxYear so the last year always shows even on off-cycle
      // starts (e.g. 2023 → 2025 → 2000, not 2023 → 2028 → wrap-skip-2025).
      const cur = localYearRef.current;
      const next =
        cur >= maxYear ? minYear : Math.min(cur + AUTOPLAY_STEP_YEARS, maxYear);
      setLocalYear(next);
      commitNow(next);
    }, ADVANCE_MS);
    return () => window.clearTimeout(id);
  }, [isPlaying, isLoading, minYear, maxYear]);

  const togglePlay = () => {
    dismissHint();
    setIsPlaying(p => !p);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    dismissHint();
    const next = Number(e.target.value);
    setLocalYear(next);
    scheduleCommit(next);
  };

  return (
    <div
      className="relative bg-figma-card/90 border border-figma-lime/30 rounded-lg shadow-figma-card backdrop-blur-sm px-3 md:px-4 py-2 md:py-3 w-full md:w-[360px]"
      role="group"
      aria-label="Above-ground biomass year selector"
    >
      {!hinted && (
        <div
          role="status"
          aria-live="polite"
          className="absolute bottom-full mb-2 right-0 z-[1100] flex items-center gap-2 pl-3 pr-2 py-2 rounded-figma-button bg-figma-card border border-figma-mint/60 shadow-figma-card whitespace-nowrap pointer-events-auto"
        >
          {/* Down arrow pointing at the slider */}
          <span
            aria-hidden="true"
            className="absolute -bottom-1.5 right-3 w-3 h-3 rotate-45 bg-figma-card border-r border-b border-figma-mint/60"
          />
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
            className="flex-shrink-0"
          >
            <path
              d="M2 6h8M7 3l3 3-3 3"
              stroke="#46ce85"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-[12px] font-roboto text-figma-text">Try scrubbing years</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              dismissHint();
            }}
            aria-label="Dismiss year-slider hint"
            className="ml-1 w-5 h-5 flex items-center justify-center rounded-full text-figma-ink-mute hover:text-figma-text hover:bg-figma-panel/60 transition-smooth"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      <div className="text-[10px] font-roboto-mono uppercase tracking-wider text-figma-ink-mute mb-1">
        Drag to scrub years
      </div>
      <div className="flex items-center gap-2 md:gap-3 mb-1.5 md:mb-2">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause year animation' : 'Play year animation'}
          aria-pressed={isPlaying}
          className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-figma-lime/20 border border-figma-lime/40 text-figma-lime hover:bg-figma-lime/30 transition-colors"
        >
          {isPlaying ? (
            <svg className="w-3 h-3 md:w-3.5 md:h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg className="w-3 h-3 md:w-3.5 md:h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4l14 8-14 8V4z" />
            </svg>
          )}
        </button>
        <div className="flex items-baseline gap-1.5 md:gap-2 flex-1 min-w-0">
          <span className="text-[10px] font-roboto-mono uppercase tracking-wider text-neutral-400">
            Year
          </span>
          <span className="text-sm md:text-lg font-roboto-mono font-bold text-figma-text tabular-nums">
            {localYear}
          </span>
        </div>
        {/* Tile-load status: a steady green dot when the visible year is
            fully painted, a pulsing dot + "Loading" while tiles are still
            streaming in. On mobile only the dot shows — the "Loading" /
            "Ready" word adds noise to a tight slider row but isn't
            critical to the affordance. */}
        <div
          className="flex items-center gap-1.5 text-[10px] font-roboto-mono uppercase tracking-wider"
          aria-live="polite"
          title={isLoading ? 'Loading tiles for this year…' : 'Tiles ready for the selected year'}
        >
          {isLoading ? (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-figma-lime animate-pulse" aria-hidden="true" />
              <span className="hidden md:inline text-figma-lime/80">Loading</span>
            </>
          ) : (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" aria-hidden="true" />
              <span className="hidden md:inline text-green-400/70">Ready</span>
            </>
          )}
        </div>
      </div>
      <input
        type="range"
        min={minYear}
        max={maxYear}
        step={1}
        value={localYear}
        onChange={handleInput}
        aria-label="Year"
        aria-valuemin={minYear}
        aria-valuemax={maxYear}
        aria-valuenow={localYear}
        // Tailwind `accent-*` sets the accent-color CSS property, which
        // themes the native thumb/fill across Chromium, Firefox, and Safari
        // without per-browser pseudo-element styling. Matches figma-lime.
        className="w-full accent-figma-lime cursor-pointer"
      />
      <div className="flex items-center justify-between text-[10px] font-roboto-mono text-neutral-500 mt-1">
        <span>{minYear}</span>
        <span>{maxYear}</span>
      </div>
    </div>
  );
};
